import { useState, useEffect } from 'react'
import { Copy, RefreshCw, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { FileUpload } from '../components/FileUpload'
import { Logo } from '../components/Logo'
import { apiService } from '../services/api'
import toast from 'react-hot-toast'

export function GeneratorPage() {
  const [uploadedFile, setUploadedFile] = useState<any>(null)
  const [visionContext, setVisionContext] = useState<any>(null)
  const [aiKeywords, setAiKeywords] = useState<string[]>([])
  const [removedAiKeywords, setRemovedAiKeywords] = useState<string[]>([]) // Track removed AI keywords
  const [contextData, setContextData] = useState<{
    mood: string[];
    platform: string;
    keywords: string;
    length: string;
  }>({
    mood: [],
    platform: '',
    keywords: '',
    length: 'short'
  })
  const [keywordChips, setKeywordChips] = useState<string[]>([])
  const [keywordInput, setKeywordInput] = useState('')
  const [regenerationCount, setRegenerationCount] = useState(new Map()) // Track regeneration count per context
  const [isGenerating, setIsGenerating] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState('')
  const [generatedCaptions, setGeneratedCaptions] = useState<any>(null)
  const [availableStyles, setAvailableStyles] = useState<any[]>([])
  const [selectedStyles] = useState<string[]>([
    'aesthetic-minimal' // Generate only one style with 7 captions
  ])
  const [inputsChanged, setInputsChanged] = useState(false) // Track if inputs changed after generation
  const [lastGeneratedInputs, setLastGeneratedInputs] = useState<string>('') // Store hash of last generated inputs
  const [suggestedHashtags, setSuggestedHashtags] = useState<string[]>([]) // Hashtag suggestions
  const [, setIsSurpriseMode] = useState(false)
  const [copiedTag, setCopiedTag] = useState<string | null>(null)
  const [hoveredCaption, setHoveredCaption] = useState<string | null>(null)
  const [mobileTab, setMobileTab] = useState<'create' | 'results'>('create') // Mobile tab navigation
  
  const moodOptions = ['Lowkey', 'Aesthetic', 'No Cap', 'Poetic', 'Bold', 'Romantic', 'Dramatic', 'Unhinged']
  const loadingStatuses = [
    'Understanding the vibe…',
    'Catching the mood…',
    'Finding the perfect words…',
    'Sprinkling some magic…',
    'Crafting your story…',
    'Adding personality…',
    'Polishing the details…',
    'Making it yours…',
    'Weaving the narrative…',
    'Capturing the essence…',
    'Painting with words…',
    'Creating the flow…',
    'Setting the tone…',
    'Building the atmosphere…',
    'Mixing creativity…',
    'Shaping the message…',
    'Tuning the energy…',
    'Blending the vibes…',
    'Styling the words…',
    'Curating the feel…',
    'Designing the voice…',
    'Composing the rhythm…',
    'Infusing character…',
    'Sculpting the mood…',
    'Channeling inspiration…'
  ]

  // Load available caption styles on component mount
  useEffect(() => {
    const loadCaptionStyles = async () => {
      try {
        const response = await apiService.getCaptionStyles()
        if ((response as any).success) {
          setAvailableStyles((response as any).data.styles)
        }
      } catch (error) {
        console.error('Failed to load caption styles:', error)
        setAvailableStyles([
          { id: 'aesthetic-minimal', name: 'Aesthetic Minimal' },
          { id: 'main-character-energy', name: 'Main Character Energy' },
          { id: 'cozy-chaos', name: 'Cozy Chaos' }
        ])
      }
    }
    
    loadCaptionStyles()
  }, [])

  // Update contextData when chips change
  useEffect(() => {
    setContextData(prev => ({
      ...prev,
      keywords: keywordChips.join(', ')
    }))
  }, [keywordChips])

  // Track input changes after first generation
  useEffect(() => {
    if (generatedCaptions && lastGeneratedInputs) {
      const currentInputs = `${keywordChips.join(',')}-${contextData.mood.join(',')}-${contextData.length}`
      if (currentInputs !== lastGeneratedInputs) {
        setInputsChanged(true)
      }
    }
  }, [keywordChips, contextData.mood, contextData.length, generatedCaptions, lastGeneratedInputs])

  const handleUploadSuccess = (fileData: any) => {
    // CRITICAL: Clear ALL previous context before setting new image data
    setUploadedFile(null)
    setVisionContext(null)
    setAiKeywords([])
    setRemovedAiKeywords([])
    setKeywordChips([])
    setKeywordInput('')
    setGeneratedCaptions(null)
    setSuggestedHashtags([])
    setMobileTab('create')

    // Now set new image data
    setUploadedFile(fileData)
    if (fileData.cocoSsdAnalysis) {
      setAiKeywords(fileData.aiKeywords || [])
      setVisionContext(fileData.visionContext)
    } else if (fileData.visionContext) {
      setVisionContext(fileData.visionContext)
      setAiKeywords(fileData.visionContext.aiKeywords || [])
    }
  }

  const handleUploadError = (error: string) => {
    console.error('Upload error:', error)
  }

  const addKeywordChip = () => {
    const keyword = keywordInput.trim()
    if (keyword && keywordChips.length < 3 && !keywordChips.includes(keyword)) {
      setKeywordChips([...keywordChips, keyword])
      setKeywordInput('')
    }
  }

  const removeKeywordChip = (index: number) => {
    setKeywordChips(keywordChips.filter((_, i) => i !== index))
  }

  const removeAiKeyword = (keyword: string) => {
    setRemovedAiKeywords([...removedAiKeywords, keyword])
  }

  const restoreAiKeyword = (keyword: string) => {
    setRemovedAiKeywords(removedAiKeywords.filter(k => k !== keyword))
  }

  // Get active AI keywords (excluding removed ones)
  const activeAiKeywords = aiKeywords.filter(k => !removedAiKeywords.includes(k))

  const handleKeywordInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addKeywordChip()
    }
  }

  const handleMoodToggle = (mood: string) => {
    // Handle "Unhinged" - it's its own mood now, not random
    if (mood.toLowerCase() === 'unhinged') {
      setContextData(prev => ({
        ...prev,
        mood: prev.mood.includes('unhinged')
          ? prev.mood.filter(m => m !== 'unhinged')
          : [...prev.mood.filter(m => m !== 'unhinged'), 'unhinged']
      }))
      setIsSurpriseMode(false)
      return
    }

    // Regular mood selection
    setIsSurpriseMode(false)
    setContextData(prev => ({
      ...prev,
      mood: prev.mood.includes(mood.toLowerCase())
        ? prev.mood.filter(m => m !== mood.toLowerCase())
        : [...prev.mood, mood.toLowerCase()]
    }))
  }

  // Generate relevant hashtags based on AI keywords and user keywords
  const generateHashtags = (aiKeywords: string[], userKeywords: string[]) => {
    const hashtags = new Set<string>()
    
    // Hashtag mapping for common objects/themes
    const hashtagMap: Record<string, string[]> = {
      // People & Relationships
      'person': ['lifestyle', 'dailylife', 'moments'],
      'romantic_couple': ['couplegoals', 'love', 'relationship', 'together'],
      'friends_group': ['squad', 'friendship', 'goodtimes', 'friends'],
      
      // Food & Drink
      'cup': ['coffee', 'coffeetime', 'coffeelover'],
      'coffee': ['coffeeholic', 'coffeegram', 'coffeeaddict'],
      'food': ['foodie', 'foodporn', 'instafood', 'yummy'],
      'cake': ['dessert', 'sweettooth', 'baking'],
      'pizza': ['pizzalover', 'foodie', 'italianfood'],
      
      // Nature & Outdoors
      'tree': ['nature', 'naturephotography', 'outdoors'],
      'sky': ['skylovers', 'sunset', 'clouds'],
      'mountain': ['mountains', 'hiking', 'adventure', 'nature'],
      'flower': ['flowers', 'floral', 'botanical', 'nature'],
      'plant': ['plants', 'greenery', 'plantlover'],
      
      // Work & Productivity
      'laptop': ['work', 'productivity', 'workspace', 'workfromhome'],
      'computer': ['tech', 'digital', 'workspace'],
      'desk': ['workspace', 'office', 'worklife'],
      
      // Animals
      'dog': ['dogsofinstagram', 'doglife', 'puppy', 'doglover'],
      'cat': ['catsofinstagram', 'catlover', 'kitty'],
      'bird': ['birds', 'birdwatching', 'nature'],
      
      // Fashion & Style
      'tie': ['fashion', 'style', 'mensfashion'],
      'handbag': ['fashion', 'style', 'accessories'],
      
      // General lifestyle
      'book': ['reading', 'bookstagram', 'bookworm'],
      'bottle': ['hydration', 'wellness', 'healthy'],
      'chair': ['interiordesign', 'homedecor', 'furniture']
    }
    
    // Add hashtags from AI keywords
    aiKeywords.forEach(keyword => {
      const cleanKeyword = keyword.toLowerCase().replace(/_/g, '')
      
      // Add the keyword itself as hashtag if it's meaningful
      if (cleanKeyword.length > 3 && !['person', 'object', 'thing'].includes(cleanKeyword)) {
        hashtags.add(cleanKeyword)
      }
      
      // Add mapped hashtags
      if (hashtagMap[keyword]) {
        hashtagMap[keyword].forEach(tag => hashtags.add(tag))
      }
    })
    
    // Add hashtags from user keywords
    userKeywords.forEach(keyword => {
      const cleanKeyword = keyword.toLowerCase().replace(/[^a-z0-9]/g, '')
      if (cleanKeyword.length > 2) {
        hashtags.add(cleanKeyword)
      }
    })
    
    // Add mood-based hashtags
    const moodHashtags: Record<string, string[]> = {
      'lowkey': ['aesthetic', 'softlife', 'vibes'],
      'aesthetic': ['maincharacter', 'aesthetic', 'curated'],
      'no cap': ['nocap', 'honest', 'facts'],
      'poetic': ['poetry', 'wordstoliveby', 'aesthetic'],
      'bold': ['confidence', 'fearless', 'strong'],
      'soft launch': ['manifesting', 'dreamy', 'romance'],
      'dramatic': ['dramatic', 'intense', 'mood'],
      'unhinged': ['chaotic', 'brainrot', 'vibes']
    }
    
    contextData.mood.forEach(mood => {
      if (moodHashtags[mood]) {
        moodHashtags[mood].forEach(tag => hashtags.add(tag))
      }
    })
    
    // Add general aesthetic hashtags
    const generalHashtags = ['aesthetic', 'instagood', 'photooftheday', 'picoftheday']
    generalHashtags.slice(0, 2).forEach(tag => hashtags.add(tag))
    
    // Convert to array and limit to 10-15 hashtags
    return Array.from(hashtags).slice(0, 12)
  }

  // Generate relevant emojis based on keywords
  const generateEmojis = (aiKeywords: string[], userKeywords: string[]) => {
    const emojis: string[] = []
    
    // Emoji mapping for common objects/themes
    const emojiMap: Record<string, string[]> = {
      // People & Relationships
      'person': ['✨', '💫'],
      'romantic_couple': ['❤️', '💕', '💑', '🥰', '💖'],
      'friends_group': ['👯', '🎉', '✨', '💫', '🌟'],
      
      // Food & Drink
      'cup': ['☕', '🍵', '☕️'],
      'coffee': ['☕', '☕️', '💛'],
      'food': ['🍽️', '😋', '🤤', '✨'],
      'cake': ['🍰', '🎂', '🧁', '✨'],
      'pizza': ['🍕', '😋', '🤤'],
      'wine': ['🍷', '🥂', '✨'],
      'bottle': ['💧', '💦', '✨'],
      
      // Nature & Outdoors
      'tree': ['🌳', '🌲', '🍃', '🌿'],
      'sky': ['☁️', '🌤️', '✨', '💫'],
      'mountain': ['⛰️', '🏔️', '🌄', '✨'],
      'flower': ['🌸', '🌺', '🌼', '🌻', '💐'],
      'plant': ['🌿', '🪴', '🌱', '💚'],
      'sunset': ['🌅', '🌇', '✨', '💛'],
      'beach': ['🏖️', '🌊', '☀️', '🌴'],
      
      // Work & Productivity
      'laptop': ['💻', '✨', '📱'],
      'computer': ['💻', '⌨️', '🖥️'],
      'desk': ['📝', '✨', '💼'],
      'book': ['📚', '📖', '✨'],
      
      // Animals
      'dog': ['🐕', '🐶', '🐾', '❤️'],
      'cat': ['🐱', '🐈', '😺', '💕'],
      'bird': ['🐦', '🕊️', '🦜'],
      
      // Fashion & Style
      'tie': ['👔', '✨', '💼'],
      'handbag': ['👜', '✨', '💫'],
      
      // Moods
      'lowkey': ['✨', '🌙', '💫'],
      'aesthetic': ['✨', '👑', '🔥'],
      'no cap': ['😏', '😄', '✨'],
      'poetic': ['🥀', '✨', '🎞️', '🌊', '🕯️'],
      'bold': ['🔥', '💥', '⚡', '✨'],
      'soft launch': ['💕', '🤍', '🦋', '✨'],
      'dramatic': ['🎭', '✨', '💫', '🌟'],
      'unhinged': ['💀', '🌪️', '🤡', '🔥'],
      'calm': ['😌', '🧘', '☮️', '✨'],
      'chill': ['😎', '✌️', '🌊', '✨'],
      'confident': ['💪', '✨', '🔥', '👑'],
      'romantic': ['💕', '❤️', '🥰', '✨']
    }
    
    // Collect emojis from AI keywords
    aiKeywords.forEach(keyword => {
      if (emojiMap[keyword]) {
        emojis.push(...emojiMap[keyword])
      }
    })
    
    // Collect emojis from user keywords
    userKeywords.forEach(keyword => {
      const lowerKeyword = keyword.toLowerCase()
      if (emojiMap[lowerKeyword]) {
        emojis.push(...emojiMap[lowerKeyword])
      }
    })
    
    // Add mood-based emojis
    contextData.mood.forEach(mood => {
      if (emojiMap[mood]) {
        emojis.push(...emojiMap[mood])
      }
    })
    
    // If no specific emojis found, add generic aesthetic ones
    if (emojis.length === 0) {
      emojis.push('✨', '💫', '🌟')
    }
    
    // Remove duplicates and return 2-3 random emojis
    const uniqueEmojis = Array.from(new Set(emojis))
    const shuffled = uniqueEmojis.sort(() => Math.random() - 0.5)
    return shuffled.slice(0, 3)
  }

  // Generate button disabled only if no file OR no mood selected
  // If no AI keywords, user can still generate with their own keywords
  const hasKeywords = activeAiKeywords.length > 0 || keywordChips.length > 0
  const isGenerateDisabled = !uploadedFile || contextData.mood.length === 0 || !hasKeywords

  const generateContextHash = () => {
    // Remove emojis and special characters before encoding
    const cleanInput = `${uploadedFile?.filename}-${keywordChips.join(',')}-${contextData.mood.join(',')}`
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '') // Remove emojis
      .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
    return btoa(cleanInput).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16)
  }

  const handleGenerateCaptions = async () => {
    if (isGenerateDisabled) return

    const currentHash = generateContextHash()
    const currentCount = regenerationCount.get(currentHash) || 0
    const newCount = currentCount + 1
    
    setRegenerationCount(prev => new Map(prev.set(currentHash, newCount)))
    
    // Clear previous captions to force refresh
    setGeneratedCaptions(null)

    setIsGenerating(true)
    setMobileTab('results') // Switch to results tab immediately so user sees loading
    
    // Shuffle and cycle through loading statuses randomly
    const shuffledStatuses = [...loadingStatuses].sort(() => Math.random() - 0.5)
    let statusIndex = 0
    const statusInterval = setInterval(() => {
      setLoadingStatus(shuffledStatuses[statusIndex])
      statusIndex = (statusIndex + 1) % shuffledStatuses.length
    }, 2000)
    // After 15s show "Studio is perfecting" — after 30s reassure user
    const slowTimer = setTimeout(() => setLoadingStatus('The Studio is perfecting your captions…'), 15000)
    const verySlowTimer = setTimeout(() => setLoadingStatus('Almost there, crafting something worth the wait…'), 30000)

    try {
      // Add intentional delay for premium feel
      await new Promise(resolve => setTimeout(resolve, 700))

      const response = await apiService.generateCaptions(
        null, // No filename - browser-only mode
        selectedStyles,
        {
          keywords: keywordChips.join(', '), // User keywords are augmentation
          mood: contextData.mood.join(', '),
          platform: contextData.platform,
          length: contextData.length,
          cocoSsdAnalysis: uploadedFile.cocoSsdAnalysis, // PRIORITY 1: COCO-SSD analysis
          visionContext, // PRIORITY 2: Server vision context fallback
          regenerationLevel: Math.min(newCount, 3),
          removedAiKeywords // Pass removed keywords to backend
        } as any
      )
      
      if ((response as any).success) {
        // Only append emojis for moods that don't generate their own
        // Moods that handle emojis in the LLM prompt: romantic, unhinged, poetic
        const selfEmojiMoods = ['lowkey', 'poetic', 'romantic', 'unhinged', 'no cap']
        const currentMood = contextData.mood[0]?.toLowerCase() || ''
        const suggestedEmojis = selfEmojiMoods.includes(currentMood) ? [] : generateEmojis(activeAiKeywords, keywordChips)
        const emojiString = suggestedEmojis.length > 0 ? ' ' + suggestedEmojis.join(' ') : ''

        const buildTransformed = (resp: any) => ({
          styles: Object.entries(resp.data.captions).map(([styleId, captions]: [string, any]) => {
            const styleInfo = availableStyles.find(s => s.id === styleId)
            return {
              id: styleId,
              name: styleInfo?.name || styleId,
              captions: captions.map((c: any) => ({
                text: c.text + emojiString,
                originalText: c.text,
                emojis: suggestedEmojis
              }))
            }
          }),
          imageAnalysis: resp.data.imageAnalysis,
          metadata: resp.data.metadata
        })

        // Count total captions returned
        const allCaptions = Object.values((response as any).data.captions).flat() as any[]
        const captionCount = allCaptions.length

        if (captionCount < 5) {
          // Auto-retry once — don't show fewer than 5
          console.warn(`⚠️ Only ${captionCount} captions returned, auto-retrying...`)
          toast.loading('Refining captions...', { id: 'retry' })
          const retryResponse = await apiService.generateCaptions(
            null, selectedStyles,
            {
              keywords: keywordChips.join(', '),
              mood: contextData.mood.join(', '),
              platform: contextData.platform,
              length: contextData.length,
              cocoSsdAnalysis: uploadedFile.cocoSsdAnalysis,
              visionContext,
              regenerationLevel: 2,
              removedAiKeywords
            } as any
          )
          toast.dismiss('retry')
          if ((retryResponse as any).success) {
            setGeneratedCaptions(buildTransformed(retryResponse as any))
          } else {
            setGeneratedCaptions(buildTransformed(response as any))
          }
        } else {
          setGeneratedCaptions(buildTransformed(response as any))
        }

        setMobileTab('results')
        const hashtags = generateHashtags(activeAiKeywords, keywordChips)
        setSuggestedHashtags(hashtags)
        const currentInputs = `${keywordChips.join(',')}-${contextData.mood.join(',')}-${contextData.length}`
        setLastGeneratedInputs(currentInputs)
        setInputsChanged(false)
        toast.success('Captions created!')
        // Track caption generation in GA4
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'caption_generated', {
            mood: contextData.mood.join('+'),
            length: contextData.length,
            caption_count: allCaptions.length
          })
        }
      } else {
        throw new Error((response as any).error || 'Caption generation failed')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate captions')
      console.error('Caption generation error:', error)
    } finally {
      clearInterval(statusInterval)
      clearTimeout(slowTimer)
      clearTimeout(verySlowTimer)
      setIsGenerating(false)
      setLoadingStatus('')
    }
  }

  const handleRegenerateCaption = async (styleId: string) => {
    if (isGenerateDisabled) return

    // Clear captions to show loading state
    setGeneratedCaptions(null)
    setIsGenerating(true)
    
    // Shuffle and cycle through loading statuses randomly
    const shuffledStatuses = [...loadingStatuses].sort(() => Math.random() - 0.5)
    let statusIndex = 0
    const statusInterval = setInterval(() => {
      setLoadingStatus(shuffledStatuses[statusIndex])
      statusIndex = (statusIndex + 1) % shuffledStatuses.length
    }, 2000)
    
    try {
      const response = await apiService.generateCaptions(
        null, // No filename - browser-only mode
        [styleId],
        {
          keywords: keywordChips.join(', '), // Use chips instead of contextData.keywords
          mood: contextData.mood.join(', '),
          platform: contextData.platform,
          length: contextData.length,
          cocoSsdAnalysis: uploadedFile.cocoSsdAnalysis, // PRIORITY 1: COCO-SSD analysis
          visionContext, // PRIORITY 2: Server vision context fallback
          isRegeneration: true,
          forceVariation: true,
          removedAiKeywords // Pass removed keywords to backend
        } as any
      )
      
      if ((response as any).success) {
        const selfEmojiMoodsRegen = ['lowkey', 'poetic', 'romantic', 'unhinged', 'no cap']
        const currentMood = contextData.mood[0]?.toLowerCase() || ''
        const regenEmojis = selfEmojiMoodsRegen.includes(currentMood) ? [] : generateEmojis(activeAiKeywords, keywordChips)
        const emojiString = regenEmojis.length > 0 ? ' ' + regenEmojis.join(' ') : ''
        
        const newCaptions = Object.entries((response as any).data.captions).map(([id, captions]: [string, any]) => {
          const styleInfo = availableStyles.find(s => s.id === id)
          return {
            id,
            name: styleInfo?.name || id,
            captions: captions.map((c: any) => ({
              text: c.text + emojiString, // Append emojis
              originalText: c.text,
              emojis: regenEmojis
            }))
          }
        })

        setGeneratedCaptions({
          styles: newCaptions,
          imageAnalysis: (response as any).data.imageAnalysis,
          metadata: (response as any).data.metadata
        })
        setMobileTab('results') // Ensure results tab is shown on mobile
        toast.success('New variation created!')
      }
    } catch (error: any) {
      toast.error('Failed to create variation')
    } finally {
      clearInterval(statusInterval)
      setIsGenerating(false)
      setLoadingStatus('')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Caption copied!')
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#0A0A0F' }}>

      {/* Ambient glow circles */}
      <div style={{
        position: 'fixed', top: '10%', left: '-10%',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
        filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
        willChange: 'transform', animation: 'breatheDrift 25s ease-in-out infinite'
      }} />
      <div style={{
        position: 'fixed', top: '40%', right: '-10%',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(224,64,251,0.08) 0%, transparent 70%)',
        filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
        willChange: 'transform', animation: 'breatheDrift 25s ease-in-out 5s infinite'
      }} />
      {/* Header */}
      <header className="flex-shrink-0 px-6 py-3" style={{
        position: 'relative',
        zIndex: 10,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <div className="mobile-header-inner max-w-7xl mx-auto flex items-center justify-between lg:justify-start">
          <Link to="/" className="hover:opacity-80 transition-opacity w-fit">
            <Logo />
          </Link>
        </div>
      </header>

      {/* Main Content - Three Zone Layout */}
      <main className="flex-1 w-full overflow-y-auto lg:overflow-hidden" style={{ position: 'relative', zIndex: 1, overflowX: 'hidden', maxWidth: '100%', height: 'auto' }}>
        {/* Mobile: Tab Navigation */}
        <div className="lg:hidden" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>

          {/* Tab Bar */}
          <div style={{ width: '95%', margin: '0 auto 12px auto', display: 'flex', gap: '8px', padding: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <button
              onClick={() => setMobileTab('create')}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: '0.85rem', fontWeight: 600,
                transition: 'all 0.2s ease',
                background: mobileTab === 'create' ? 'linear-gradient(135deg, rgba(142,143,250,0.2), rgba(224,64,251,0.15))' : 'transparent',
                color: mobileTab === 'create' ? '#D1D1F7' : 'rgba(255,255,255,0.4)',
                boxShadow: mobileTab === 'create' ? '0 0 12px rgba(142,143,250,0.2)' : 'none',
                borderBottom: mobileTab === 'create' ? '1px solid rgba(142,143,250,0.4)' : '1px solid transparent'
              }}
            >
              📸 Create
            </button>
            <button
              onClick={() => setMobileTab('results')}
              style={{
                flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: '0.85rem', fontWeight: 600,
                transition: 'all 0.2s ease',
                background: mobileTab === 'results' ? 'linear-gradient(135deg, rgba(224,64,251,0.2), rgba(142,143,250,0.15))' : 'transparent',
                color: mobileTab === 'results' ? '#e040fb' : 'rgba(255,255,255,0.4)',
                boxShadow: mobileTab === 'results' ? '0 0 12px rgba(224,64,251,0.2)' : 'none',
                borderBottom: mobileTab === 'results' ? '1px solid rgba(224,64,251,0.4)' : '1px solid transparent',
                position: 'relative'
              }}
            >
              ✨ Results
              {generatedCaptions && (
                <span style={{ position: 'absolute', top: '6px', right: '10px', width: '8px', height: '8px', borderRadius: '50%', background: '#e040fb', boxShadow: '0 0 6px rgba(224,64,251,0.8)' }} />
              )}
            </button>
          </div>

          {/* CREATE TAB */}
          {mobileTab === 'create' && (
            <>
              <div className="mobile-glass-stack">
                {/* Upload */}
                <div className="mobile-section">
                  <FileUpload
                    onUploadSuccess={handleUploadSuccess}
                    onUploadError={handleUploadError}
                    initialFile={uploadedFile}
                  />
                  {uploadedFile && aiKeywords.length === 0 && (
                    <div className="mt-2 p-2" style={{ background: 'rgba(142,143,250,0.06)', border: '1px solid rgba(142,143,250,0.2)', borderRadius: '10px' }}>
                      <p className="text-xs font-medium" style={{ color: '#D1D1F7' }}>💡 No AI tags — add keywords below</p>
                    </div>
                  )}
                  {aiKeywords.length > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-1">
                        {activeAiKeywords.map((keyword, index) => {
                          return (
                            <div key={index} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(142,143,250,0.08)', border: '1px solid rgba(142,143,250,0.3)', color: '#D1D1F7' }}>
                              <span>{keyword}</span>
                              <button onClick={() => removeAiKeyword(keyword)} style={{ color: 'rgba(255,255,255,0.4)' }}><X className="h-3 w-3" /></button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Keywords */}
                <div className="mobile-section">
                  {keywordChips.length < 3 && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={handleKeywordInputKeyDown}
                        placeholder="Add keywords (aura, lore...)"
                        className="input-field"
                        style={{ flex: 1 }}
                      />
                      {keywordInput.trim() && (
                        <button type="button" onClick={addKeywordChip}
                          style={{ background: 'rgba(142,143,250,0.15)', border: '1px solid rgba(142,143,250,0.3)', color: '#D1D1F7', borderRadius: '8px', padding: '0 12px', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
                          Add
                        </button>
                      )}
                    </div>
                  )}
                  {keywordChips.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {keywordChips.map((chip, index) => (
                        <div key={index} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(142,143,250,0.08)', border: '1px solid rgba(142,143,250,0.3)', color: '#D1D1F7' }}>
                          <span>{chip}</span>
                          <button onClick={() => removeKeywordChip(index)} style={{ color: 'rgba(255,255,255,0.4)' }}><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mood */}
                <div className="mobile-section">
                  <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase' }}>Vibe</p>
                  <div className="mobile-mood-bar">
                    {moodOptions.map((mood) => {
                      const isActive = contextData.mood.includes(mood.toLowerCase())
                      return (
                        <button key={mood} type="button" onClick={() => handleMoodToggle(mood)}
                          style={isActive ? { background: 'linear-gradient(135deg, #e040fb 0%, #7c3aed 100%)', color: '#ffffff', border: '1px solid rgba(224,64,251,0.5)', boxShadow: '0 0 15px rgba(212,0,255,0.4)', borderRadius: '8px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 500 }
                            : { background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '0.4rem 0.8rem', fontSize: '0.78rem' }}>
                          {mood}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Length */}
                <div className="mobile-section">
                  <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase' }}>Length</p>
                  <div className="mobile-length-bar">
                    {['Short', 'Long', 'Mixed'].map((length) => (
                      <button key={length} type="button" onClick={() => setContextData(prev => ({ ...prev, length: length.toLowerCase() }))}
                        style={contextData.length === length.toLowerCase()
                          ? { background: 'linear-gradient(135deg, #e040fb 0%, #7c3aed 100%)', color: '#ffffff', border: '1px solid rgba(224,64,251,0.5)', boxShadow: '0 0 15px rgba(212,0,255,0.4)', borderRadius: '8px', padding: '0.5rem', fontSize: '0.8rem', fontWeight: 500 }
                          : { background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '0.5rem', fontSize: '0.8rem' }}>
                        {length}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div style={{ width: '95%', margin: '16px auto 32px auto' }}>
                {/* Hint text showing what's needed */}
                {!uploadedFile && (
                  <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', marginBottom: '8px' }}>
                    📸 Upload a photo first
                  </p>
                )}
                {uploadedFile && contextData.mood.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', marginBottom: '8px' }}>
                    ✨ Select a mood to continue
                  </p>
                )}
                <button
                  onClick={handleGenerateCaptions}
                  disabled={isGenerateDisabled || isGenerating}
                  className="mobile-generate-btn"
                  style={isGenerateDisabled || isGenerating ? {
                    background: 'rgba(255,255,255,0.04)',
                    color: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    cursor: 'not-allowed',
                    opacity: 0.5,
                    boxShadow: 'none'
                  } : {
                    background: 'linear-gradient(90deg, #FF00FF 0%, #8E8FFA 100%)',
                    color: '#ffffff',
                    border: 'none',
                    boxShadow: '0 0 25px rgba(212,0,255,0.5)',
                    cursor: 'pointer'
                  }}
                >
                  {isGenerating ? 'Generating...' : 'GENERATE CAPTIONS ✨'}
                </button>
              </div>
            </>
          )}

          {/* RESULTS TAB */}
          {mobileTab === 'results' && (
            <div style={{ width: '95%', margin: '0 auto', paddingBottom: '32px' }}>
              {isGenerating ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Logo showText={false} className="animate-pulse" />
                  <p style={{ color: '#D1D1F7', marginTop: '16px', fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: '1rem', fontWeight: 500 }}>{loadingStatus || 'Generating captions...'}</p>
                  <p style={{ color: 'rgba(255,255,255,0.3)', marginTop: '8px', fontSize: '0.8rem' }}>This takes about 10–20 seconds</p>
                </div>
              ) : !generatedCaptions ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <p style={{ fontSize: '2rem', marginBottom: '12px' }}>✨</p>
                  <p style={{ color: '#D1D1F7', fontFamily: "'Space Grotesk','Inter',sans-serif", marginBottom: '8px' }}>No captions yet</p>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Go to Create tab and generate captions</p>
                  <button onClick={() => setMobileTab('create')} style={{ marginTop: '20px', background: 'rgba(142,143,250,0.1)', border: '1px solid rgba(142,143,250,0.3)', color: '#D1D1F7', padding: '10px 24px', borderRadius: '999px', cursor: 'pointer', fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: '0.85rem' }}>
                    ← Back to Create
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Hashtags */}
                  {suggestedHashtags.length > 0 && (
                    <div style={{ background: 'rgba(142,143,250,0.05)', border: '1px solid rgba(142,143,250,0.15)', borderRadius: '16px', padding: '14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <p style={{ color: '#D1D1F7', fontSize: '0.8rem', fontWeight: 600 }}>🏷️ Hashtags</p>
                        <button onClick={() => { navigator.clipboard.writeText(suggestedHashtags.map(t => `#${t}`).join(' ')); toast.success('Copied!') }} style={{ color: '#8E8FFA', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer' }}>Copy All</button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {suggestedHashtags.map((tag, i) => (
                          <button key={i} onClick={() => { navigator.clipboard.writeText(`#${tag}`); toast.success(`#${tag} copied!`); setCopiedTag(tag); setTimeout(() => setCopiedTag(null), 800) }}
                            style={copiedTag === tag ? { background: '#e040fb', color: '#fff', border: '1px solid #e040fb', borderRadius: '999px', padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer' }
                              : { background: 'rgba(142,143,250,0.08)', color: '#D1D1F7', border: '1px solid rgba(142,143,250,0.2)', borderRadius: '999px', padding: '3px 10px', fontSize: '0.75rem', cursor: 'pointer' }}>
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Captions */}
                  {generatedCaptions.styles.map((style: any, si: number) => (
                    style.captions.map((caption: any, ci: number) => {
                      const captionText = typeof caption === 'string' ? caption : caption.text
                      return (
                        <div key={`${si}-${ci}`} style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '16px', transition: 'all 0.2s' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: '0.65rem', color: '#8E8FFA', background: 'rgba(142,143,250,0.1)', border: '1px solid rgba(142,143,250,0.2)', borderRadius: '999px', padding: '2px 8px', display: 'inline-block', marginBottom: '8px' }}>#{ci + 1}</span>
                              <p style={{ color: '#ffffff', lineHeight: 1.6, margin: 0 }}>{captionText}</p>
                            </div>
                            <button onClick={() => copyToClipboard(captionText)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', flexShrink: 0, padding: '4px' }}>
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )
                    })
                  ))}

                  {/* Regenerate */}
                  <button onClick={() => { setMobileTab('create'); setGeneratedCaptions(null) }}
                    style={{ width: '100%', padding: '12px', borderRadius: '999px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: '0.85rem', marginTop: '8px' }}>
                    ← Edit & Regenerate
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop: three column layout */}
        <div className="hidden lg:grid lg:grid-cols-12 gap-6 h-full max-w-7xl mx-auto px-6">
          
          {/* LEFT: Image Zone */}
          <div className="lg:col-span-4 flex flex-col min-h-0" style={{ position: 'relative', zIndex: 1 }}>
            <div className="mobile-glass-stack flex-1 flex flex-col overflow-y-auto min-h-0 transition-shadow duration-300" style={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '24px',
              padding: '1.25rem'
            }}>
              <h2 className="mobile-hide-header text-lg font-semibold mb-4 flex-shrink-0 lg:block" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", color: '#ffffff' }}>Upload Your Image</h2>
              <p className="mobile-label hidden">📸 Upload</p>
              <div className="flex-shrink-0">
                <FileUpload
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                  initialFile={uploadedFile}
                />
              </div>

              {/* No AI tags detected prompt */}
              {uploadedFile && aiKeywords.length === 0 && (
                <div className="mt-3 p-3 flex-shrink-0" style={{ background: 'rgba(142,143,250,0.06)', border: '1px solid rgba(142,143,250,0.2)', borderRadius: '12px' }}>
                  <p className="text-sm font-medium" style={{ color: '#D1D1F7' }}>💡 No AI tags detected</p>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Add your own keywords in Creative Inputs to generate captions.</p>
                </div>
              )}
              
              {/* AI Keywords Display - Moved here */}
              {aiKeywords.length > 0 && (
                <div className="space-y-3 mt-6 flex-shrink-0">
                  <label className="block text-sm font-medium" style={{ color: '#D1D1F7', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
                    AI detected from your photo
                  </label>
                  
                  {activeAiKeywords.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {activeAiKeywords.map((keyword, index) => {
                        return (
                          <div key={index} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 transform hover:scale-105 animate-scale-in" style={{
                            background: 'rgba(142,143,250,0.08)',
                            border: '1px solid rgba(142,143,250,0.3)',
                            color: '#D1D1F7',
                            animationDelay: `${index * 50}ms`
                          }}>
                            <span>✨</span>
                            <span>{keyword}</span>
                            <button
                              onClick={() => removeAiKeyword(keyword)}
                              className="hover:bg-[#D4AF37]/20 rounded-full p-0.5 transition-colors duration-200 ml-1"
                              title="Remove this keyword"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  ) : aiKeywords.length === 0 && uploadedFile ? (
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(142,143,250,0.06)', border: '1px solid rgba(142,143,250,0.2)' }}>
                      <p className="text-sm font-medium" style={{ color: '#D1D1F7' }}>🤔 Couldn't detect anything from this photo</p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Add your own keywords in <span className="font-semibold" style={{ color: '#8E8FFA' }}>"Creative Inputs"</span> to generate captions.</p>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg" style={{ background: 'rgba(224,64,251,0.06)', border: '1px solid rgba(224,64,251,0.2)' }}>
                      <p className="text-sm font-medium" style={{ color: '#e040fb' }}>⚠️ All AI keywords removed</p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Restore keywords below or add your own to generate captions</p>
                    </div>
                  )}
                  
                  {/* Removed Keywords Section */}
                  {removedAiKeywords.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(142,143,250,0.05)', border: '1px solid rgba(142,143,250,0.15)' }}>
                      <p className="text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>Removed keywords (click to restore):</p>
                      <div className="flex flex-wrap gap-2">
                        {removedAiKeywords.map((keyword, index) => (
                          <button
                            key={index}
                            onClick={() => restoreAiKeyword(keyword)}
                            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 transform hover:scale-105"
                            style={{ background: 'rgba(142,143,250,0.08)', border: '1px solid rgba(142,143,250,0.2)', color: 'rgba(255,255,255,0.5)' }}
                            title="Click to restore"
                          >
                            <span>{keyword}</span>
                            <span className="text-[10px]">↺</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CENTER: Creative Inputs */}
          <div className="lg:col-span-4 flex flex-col min-h-0" style={{ position: 'relative', zIndex: 1 }}>
            <div className="mobile-glass-stack flex-1 overflow-y-auto min-h-0 transition-shadow duration-300" style={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '24px',
              padding: '1.25rem'
            }}>
              <h2 className="mobile-hide-header text-lg font-semibold mb-6" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", color: '#ffffff' }}>Creative Inputs</h2>
              
              {/* Keywords Input */}
              <div className="space-y-4 mb-8">
                <label className="block text-sm font-medium" style={{ color: '#D1D1F7', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
                  What's in this photo? (optional - adds to AI keywords)
                </label>
                
                <div className="mobile-input-group space-y-3">
                  {/* Keyword Chips */}
                  {keywordChips.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {keywordChips.map((chip, index) => (
                        <div key={index} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 transform hover:scale-105 animate-scale-in" style={{
                          background: 'rgba(142,143,250,0.08)',
                          border: '1px solid rgba(142,143,250,0.3)',
                          color: '#D1D1F7',
                          animationDelay: `${index * 50}ms`
                        }}>
                          <span>{chip}</span>
                          <button
                            onClick={() => removeKeywordChip(index)}
                            className="rounded-full p-0.5 transition-colors duration-200"
                            style={{ color: 'rgba(255,255,255,0.4)' }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Keyword Input */}
                  {keywordChips.length < 3 && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={handleKeywordInputKeyDown}
                        placeholder="Add keywords (aura, lore...)"
                        className="input-field flex-1"
                      />
                    </div>
                  )}
                </div>
                
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Add 1–3 keywords to augment the AI analysis (optional)
                </p>
              </div>

              {/* Mood Input */}
              <div className="space-y-4">
                <label className="block text-sm font-medium" style={{ color: '#D1D1F7', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
                  How should this feel?
                </label>
                
                <div className="mobile-mood-bar grid grid-cols-2 gap-2">
                  {moodOptions.map((mood) => {
                    // Only show "Surprise Me" as active when in surprise mode
                    // Don't show the underlying mood as active
                    const isActive = contextData.mood.includes(mood.toLowerCase())
                    
                    return (
                      <button
                        key={mood}
                        type="button"
                        onClick={() => handleMoodToggle(mood)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                          isActive
                            ? ''
                            : ''
                        }`}
                        style={isActive ? {
                          background: 'linear-gradient(135deg, #e040fb 0%, #7c3aed 100%)',
                          color: '#ffffff',
                          border: '1px solid rgba(224,64,251,0.5)',
                          boxShadow: '0 0 15px rgba(212,0,255,0.4)',
                          transition: 'all 0.3s ease'
                        } : {
                          background: 'transparent',
                          color: 'rgba(255,255,255,0.6)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {mood}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Length Option */}
              <div className="space-y-4 mt-12">
                <label className="block text-sm font-medium" style={{ color: '#D1D1F7', fontFamily: "'Space Grotesk','Inter',sans-serif" }}>
                  Caption length
                </label>
                
                <div className="mobile-length-bar grid grid-cols-3 gap-2">
                  {['Short', 'Long', 'Mixed'].map((length) => (
                    <button
                      key={length}
                      type="button"
                      onClick={() => setContextData(prev => ({ ...prev, length: length.toLowerCase() }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 active:scale-95`}
                      style={contextData.length === length.toLowerCase() ? {
                        background: 'linear-gradient(135deg, #e040fb 0%, #7c3aed 100%)',
                        color: '#ffffff',
                        border: '1px solid rgba(224,64,251,0.5)',
                        boxShadow: '0 0 15px rgba(212,0,255,0.4)',
                        transition: 'all 0.3s ease'
                      } : {
                        background: 'transparent',
                        color: 'rgba(255,255,255,0.6)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {length}
                    </button>
                  ))}
                </div>
              </div>

              {/* Helper Text */}
              <div className="mt-8 p-4 rounded-lg" style={{ background: 'rgba(142,143,250,0.04)', border: '1px solid rgba(142,143,250,0.1)' }}>
                <p className="text-sm text-center" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  AI analyzes your photo first • Your inputs refine the style
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: Output Display */}
          <div className="lg:col-span-4 flex flex-col min-h-0" style={{ position: 'relative', zIndex: 1 }}>
            <div className={`flex-1 flex flex-col min-h-0 transition-all duration-300 ${isGenerating ? 'animate-breathe' : ''}`} style={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '24px',
              padding: '1.25rem'
            }}>
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h2 className="mobile-hide-header text-lg font-semibold mb-4 flex-shrink-0" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", color: '#ffffff' }}>Generated Captions</h2>
                {inputsChanged && generatedCaptions && !isGenerating && (
                  <button
                    onClick={handleGenerateCaptions}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #e040fb 0%, #7c3aed 100%)',
                      color: '#ffffff',
                      border: '1px solid rgba(224,64,251,0.5)',
                      boxShadow: '0 0 15px rgba(212,0,255,0.4)'
                    }}
                    title="Regenerate with new inputs"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Regenerate</span>
                  </button>
                )}
              </div>
              
              <div className="flex-1 min-h-0" style={{ position: 'relative' }}>
                {/* Fade-out mask at bottom */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '80px',
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(10,10,15,0.95) 100%)',
                  pointerEvents: 'none',
                  zIndex: 2
                }} />
                <div className="overflow-y-auto h-full" style={{ paddingBottom: '60px' }}>
              {isGenerating ? (
                <div className="flex items-center justify-center text-center h-full">
                  <div className="space-y-4">
                    <div className="flex items-center justify-center">
                      <Logo showText={false} className="animate-pulse" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-foreground">{loadingStatus || 'Generating captions...'}</p>
                    </div>
                  </div>
                </div>
              ) : !uploadedFile ? (
                <div className="flex items-center justify-center text-center h-full">
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-card/50 border border-border rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C20.832 18.477 19.246 18 17.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-medium text-foreground">Upload an image to get started</p>
                      <p className="text-sm text-muted">Your captions will appear here</p>
                    </div>
                  </div>
                </div>
              ) : generatedCaptions ? (
                <div className="space-y-4 pr-2">
                  {/* Hashtag Suggestions */}
                  {suggestedHashtags.length > 0 && (
                    <div className="p-4 rounded-lg animate-slide-up" style={{
                      background: 'rgba(142,143,250,0.05)',
                      border: '1px solid rgba(142,143,250,0.15)'
                    }}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold" style={{ color: '#D1D1F7' }}>🏷️ Suggested Hashtags</p>
                        <button
                          onClick={() => {
                            const hashtagText = suggestedHashtags.map(tag => `#${tag}`).join(' ')
                            navigator.clipboard.writeText(hashtagText)
                            toast.success('Hashtags copied!')
                          }}
                          className="text-xs font-medium transition-colors"
                          style={{ color: '#8E8FFA' }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')}
                          onMouseLeave={e => (e.currentTarget.style.color = '#8E8FFA')}
                        >
                          Copy All
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {suggestedHashtags.map((tag, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              navigator.clipboard.writeText(`#${tag}`)
                              toast.success(`#${tag} copied!`)
                              setCopiedTag(tag)
                              setTimeout(() => setCopiedTag(null), 800)
                            }}
                            className="text-xs px-2 py-1 rounded-full font-medium transition-all duration-200 transform hover:scale-105"
                            style={copiedTag === tag ? {
                              background: '#e040fb',
                              color: '#ffffff',
                              border: '1px solid #e040fb',
                              boxShadow: '0 0 12px rgba(224,64,251,0.6)'
                            } : {
                              background: 'rgba(142,143,250,0.08)',
                              color: '#D1D1F7',
                              border: '1px solid rgba(142,143,250,0.2)',
                              boxShadow: 'none'
                            }}
                            onMouseEnter={e => {
                              if (copiedTag !== tag) {
                                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 10px rgba(224,64,251,0.35)'
                                ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(224,64,251,0.4)'
                              }
                            }}
                            onMouseLeave={e => {
                              if (copiedTag !== tag) {
                                (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'
                                ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(142,143,250,0.2)'
                              }
                            }}
                            title="Click to copy"
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>Click any hashtag to copy it individually</p>
                    </div>
                  )}
                  
                  {generatedCaptions.styles.map((style: any, styleIndex: number) => (
                    <div key={styleIndex} className="space-y-3">
                      {style.captions.map((caption: any, captionIndex: number) => {
                        const captionText = typeof caption === 'string' ? caption : caption.text
                        
                        return (
                          <div 
                            key={`${styleIndex}-${captionIndex}`}
                            className="caption-card animate-slide-up"
                            onMouseEnter={() => setHoveredCaption(`${styleIndex}-${captionIndex}`)}
                            onMouseLeave={() => setHoveredCaption(null)}
                            style={{
                              animationDelay: `${captionIndex * 50}ms`,
                              padding: '1rem',
                              background: hoveredCaption === `${styleIndex}-${captionIndex}`
                                ? 'linear-gradient(180deg, rgba(142,143,250,0.08) 0%, rgba(255,255,255,0.02) 100%)'
                                : 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)',
                              border: hoveredCaption === `${styleIndex}-${captionIndex}`
                                ? '1px solid rgba(142,143,250,0.3)'
                                : '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '16px',
                              boxShadow: hoveredCaption === `${styleIndex}-${captionIndex}`
                                ? '0 0 20px rgba(142,143,250,0.12)'
                                : 'none',
                              transition: 'all 0.25s ease'
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{
                                    color: '#8E8FFA',
                                    background: 'rgba(142,143,250,0.1)',
                                    border: '1px solid rgba(142,143,250,0.2)'
                                  }}>
                                    #{captionIndex + 1}
                                  </span>
                                </div>
                                <p className="mb-2 leading-relaxed" style={{ color: '#ffffff' }}>{captionText}</p>
                              </div>
                              <div className="flex gap-1">
                                {/* Copy button with tooltip */}
                                <div style={{ position: 'relative' }} className="group">
                                  <button
                                    onClick={() => copyToClipboard(captionText)}
                                    className="p-2 rounded transition-all duration-200 transform hover:scale-110 active:scale-95"
                                    style={{ color: 'rgba(255,255,255,0.4)' }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#D1D1F7')}
                                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                  <div className="group-hover:opacity-100 opacity-0 pointer-events-none transition-opacity duration-200" style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    marginBottom: '4px',
                                    background: 'rgba(20,20,30,0.95)',
                                    border: '1px solid rgba(142,143,250,0.3)',
                                    color: '#D1D1F7',
                                    fontSize: '11px',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    Copy to clipboard
                                  </div>
                                </div>
                                {/* Regenerate button with rotation */}
                                <button
                                  onClick={() => handleRegenerateCaption(style.id)}
                                  className="p-2 rounded transition-all duration-300 active:scale-95"
                                  style={{ color: 'rgba(255,255,255,0.4)' }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.color = '#8E8FFA'
                                    const icon = e.currentTarget.querySelector('svg')
                                    if (icon) icon.style.transform = 'rotate(90deg)'
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.color = 'rgba(255,255,255,0.4)'
                                    const icon = e.currentTarget.querySelector('svg')
                                    if (icon) icon.style.transform = 'rotate(0deg)'
                                  }}
                                  disabled={isGenerating}
                                  title="Generate new set"
                                >
                                  <RefreshCw className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} style={{ transition: 'transform 0.3s ease' }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-center h-full">
                  <div className="space-y-6 w-full max-w-sm">
                    <div style={{ position: 'relative', width: '64px', height: '64px', margin: '0 auto' }}>
                      {/* Halo */}
                      <div style={{
                        position: 'absolute',
                        inset: '-8px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(142,143,250,0.10) 0%, transparent 70%)',
                        pointerEvents: 'none'
                      }} />
                      {/* Icon container */}
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'rgba(142,143,250,0.06)',
                        border: '1px solid rgba(142,143,250,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg className="w-8 h-8" fill="none" stroke="#D1D1F7" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <p className="text-lg font-medium" style={{
                        background: 'linear-gradient(90deg, #ffffff 0%, #D1D1F7 100%)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>Ready to create captions</p>
                      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Add keywords and mood to get started</p>
                    </div>
                    
                    {/* Generate Button */}
                    <button
                      onClick={handleGenerateCaptions}
                      disabled={isGenerateDisabled || isGenerating}
                      className="w-full lg:w-full py-4 px-6 rounded-lg font-bold transition-all duration-300 transform active:scale-95 mobile-generate-btn"
                      style={isGenerateDisabled || isGenerating ? {
                        background: 'rgba(255,255,255,0.06)',
                        color: 'rgba(255,255,255,0.3)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        cursor: 'not-allowed'
                      } : {
                        background: 'linear-gradient(90deg, #FF00FF 0%, #8E8FFA 100%)',
                        color: '#ffffff',
                        border: 'none',
                        boxShadow: '0 0 25px rgba(212,0,255,0.5)',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => {
                        if (!isGenerateDisabled && !isGenerating) {
                          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
                          ;(e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 50px rgba(212,0,255,1)'
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isGenerateDisabled && !isGenerating) {
                          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
                          ;(e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 25px rgba(212,0,255,0.5)'
                        }
                      }}
                    >
                      <span className="hidden lg:inline">Generate Captions ✨</span>
                      <span className="lg:hidden">GENERATE CAPTIONS ✨</span>
                    </button>
                  </div>
                </div>
              )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}